import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Button, Card, Divider, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import SweepBase from '../../../../models/Sweep';
import SweepService from '../../../../services/SweepService';
import SweepsTable from './subcomponents/SweepsTable';
import SweepsCreate from './subcomponents/SweepsCreate';
import WarehouseService from '../../../../services/WarehouseService';
import PalletDeviceService from '../../../../services/PalletDeviceService';
import { WarehouseSummary } from '../../../../models/Warehouse';
import PalletDevice from '../../../../models/PalletDevice';
import SweepsControls from './SweepsControls';
import { useNavigate } from 'react-router-dom';
import PermissionService from '../../../../services/PermissionService';
import UserContext from '../../../../context/UserContext';
import { useLanguage } from '../../../../context/LanguageContext';



const Sweeps: React.FC = () => {
  const areStringArraysEqual = (arrA: string[], arrB: string[]) => {
    if (arrA.length !== arrB.length) {
      return false;
    }
    return arrA.every((value, index) => value === arrB[index]);
  };

  const isMobile = useContext(UserContext).isMobile;
  const { language } = useLanguage();
  const [warehouses, setWarehouses] = useState<WarehouseSummary[]>([]);
  const [devices, setDevices] = useState<PalletDevice[]>([]);
  const [sweeps, setSweeps] = useState<SweepBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nowProcessing, setNowProcessing] = useState<number[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [countingNumberFilter, setCountingNumberFilter] = useState<string>('');
  const [writePermission, setWritePermissions] = useState<Boolean>(false);
  const [securityPermission, setSecurityPermission] = useState<Boolean>(false);
  const [count, setCount] = useState<number>(10);
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  const onPageChange = useCallback((page: number, pageSize: number) => {
    setPage(page)
    setFilterLoading(true)
    setPageSize(pageSize)
  }, []);

  const updateContent = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    SweepService.getAll(navigate, page, pageSize, warehouseFilter, statusFilter, countingNumberFilter, false, abortController.signal).then((sweeps) => {
      if (sweeps == null) {
        return;
      }
      const processing = [];
      for (let sweep of sweeps.results) {
        if (sweep.warehouse && (sweep.status === 'processing' || sweep.status === 'creating')) {
          processing.push(sweep.warehouse.idx);
        }
      }
      setCount(sweeps.total_count);
      setNowProcessing(processing);
      setSweeps(sweeps.results);
      setLoading(false);
      setFilterLoading(false);
    });
    // eslint-disable-next-line
  }, [navigate, page, pageSize, warehouseFilter, statusFilter, countingNumberFilter]);

  const updateServices = useCallback(() => {
    Promise.all([
      WarehouseService.getAll(navigate),
      PalletDeviceService.getAll(),
      PermissionService.getSecurityWritePermission(),
      PermissionService.getSweepWritePermission(),
    ]).then(([warehousesData, devicesData, securityPermissionData, writePermissionData]) => {
      if (warehousesData == null || devicesData == null) {
        return;
      }
      setSecurityPermission(securityPermissionData);
      setWritePermissions(writePermissionData);
      setWarehouses(warehousesData);
      setDevices(devicesData);
      setLoading(false);
    });
  }, [navigate]);

  const onApplyFilters = useCallback(
    (warehouse: string[], countingNumber: string, status: string[]) => {
      const warehouseChanged = !areStringArraysEqual(warehouseFilter, warehouse);
      const statusChanged = !areStringArraysEqual(statusFilter, status);
      const countingNumberChanged = countingNumberFilter !== countingNumber;
      const pageChanged = page !== 1;

      if (!warehouseChanged && !statusChanged && !countingNumberChanged && !pageChanged) {
        return;
      }

      if (pageChanged) {
        setPage(1);
      }
      if (warehouseChanged) {
        setWarehouseFilter(warehouse);
      }
      if (statusChanged) {
        setStatusFilter(status);
      }
      if (countingNumberChanged) {
        setCountingNumberFilter(countingNumber);
      }
      setFilterLoading(true);
    },
    [warehouseFilter, statusFilter, countingNumberFilter, page]
  );

  useEffect(() => {
    updateContent();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [updateContent]);

  useEffect(() => {
    updateServices();
    const interval = setInterval(() => {
      updateServices();
    }, 600000);
    return () => clearInterval(interval);
  }, [updateServices]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateContent();
    }, 600000);
    return () => clearInterval(interval);
  }, [updateContent]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const submitLoading = (state: boolean) => {
    setLoading(state);
  };

  return (
    <Spin spinning={loading}>
      <Card
        title={language === "es" ? "Barridos" : "Sweeps"}
        extra={
          <Button
            type="primary"
            onClick={showModal}
            icon={<PlusOutlined />}
            shape="round"
            disabled={!writePermission}
            style={{ margin: isMobile ? '0 5px' : '0' }}
          >
            {language === "es" ? "Nuevo" : "New"}
          </Button>
        }
        style={{
          margin: isMobile ? '10px' : '20px',
          padding: isMobile ? '10px' : '20px',
        }}
      >
        <SweepsCreate
          isVisible={isModalOpen}
          onClose={closeModal}
          onUpdateContent={updateContent}
          warehouses={warehouses}
          devices={devices}
          nowProcessing={nowProcessing}
          sweeps={sweeps}
          onSubmitLoading={submitLoading}
        />
        <SweepsControls
          loading={filterLoading}
          onApply={onApplyFilters}
          warehouses={warehouses}
        />
        <Divider />
        <SweepsTable
          writePermission={writePermission}
          securityPermission={securityPermission}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          total={count}
          sweeps={sweeps}
          onUpdateContent={updateContent}
          isMobile={isMobile}
          loading={filterLoading}
        />
      </Card>
    </Spin>
  );
};

export default Sweeps;
